import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getPaddleEnvironment } from "@/lib/paddle";

interface Entitlement {
  loading: boolean;
  hasAccess: boolean;
  isSubscribed: boolean;
  isInTrial: boolean;
  trialEndsAt: Date | null;
  trialDaysLeft: number;
  tier: string | null;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: Date | null;
  refresh: () => Promise<void>;
}

export function useEntitlement(): Entitlement {
  const [state, setState] = useState<Omit<Entitlement, "refresh">>({
    loading: true,
    hasAccess: false,
    isSubscribed: false,
    isInTrial: false,
    trialEndsAt: null,
    trialDaysLeft: 0,
    tier: null,
    cancelAtPeriodEnd: false,
    currentPeriodEnd: null,
  });

  const load = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }

    const env = getPaddleEnvironment();

    const [subRes, clientRes] = await Promise.all([
      supabase
        .from("subscriptions")
        .select("product_id, status, current_period_end, cancel_at_period_end")
        .eq("user_id", session.user.id)
        .eq("environment", env)
        .maybeSingle(),
      supabase
        .from("clients")
        .select("trial_ends_at")
        .eq("user_id", session.user.id)
        .order("created_at", { ascending: true })
        .limit(1)
        .maybeSingle(),
    ]);

    const sub = subRes.data;
    const now = new Date();
    const periodEnd = sub?.current_period_end ? new Date(sub.current_period_end) : null;
    const isSubscribed = !!sub
      && ["active", "trialing"].includes(sub.status)
      && (!periodEnd || periodEnd > now);

    const trialEndsAt = clientRes.data?.trial_ends_at ? new Date(clientRes.data.trial_ends_at) : null;
    const isInTrial = !!trialEndsAt && trialEndsAt > now;
    const trialDaysLeft = isInTrial && trialEndsAt
      ? Math.max(0, Math.ceil((trialEndsAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)))
      : 0;

    setState({
      loading: false,
      hasAccess: isSubscribed || isInTrial,
      isSubscribed,
      isInTrial,
      trialEndsAt,
      trialDaysLeft,
      tier: sub?.product_id || null,
      cancelAtPeriodEnd: !!sub?.cancel_at_period_end,
      currentPeriodEnd: periodEnd,
    });
  };

  useEffect(() => {
    load();
    const channel = supabase
      .channel(`entitlement-subs-${Math.random().toString(36).slice(2)}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "subscriptions" }, () => load())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  return { ...state, refresh: load };
}
